import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is already logged in on app start
  useEffect(() => {
    console.log('UserContext - Checking for existing user session...');
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    console.log('UserContext - Found in localStorage:', { token: !!token, userData: !!userData });
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log('UserContext - Restoring user session:', parsedUser);
        setUser(parsedUser);
        setIsAuthenticated(true);
        // restore axios defaults
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const storedCid = JSON.parse(localStorage.getItem('activeCompanyId') || 'null') || JSON.parse(localStorage.getItem('company_id') || 'null');
          const companyId = parsedUser?.company_id || storedCid;
          if (companyId) {
            axios.defaults.headers.common['company_id'] = String(companyId);
            try { localStorage.setItem('company_id', String(companyId)); } catch (e) {}
            try { localStorage.setItem('activeCompanyId', String(companyId)); } catch (e) {}
          }
          // install interceptor to ensure company_id is present on write requests
          const interceptorId = axios.interceptors.request.use((config) => {
            try {
              const method = (config.method || '').toLowerCase();
              if (['post', 'put', 'patch'].includes(method)) {
                const cid = axios.defaults.headers.common['company_id'] || localStorage.getItem('company_id');
                if (cid) {
                  // if FormData, append
                  if (config.data instanceof FormData) {
                    if (!config.data.get('company_id')) config.data.append('company_id', String(cid));
                  } else if (config.headers && config.headers['Content-Type'] && config.headers['Content-Type'].includes('application/json')) {
                    const data = config.data || {};
                    if (data && typeof data === 'object' && !Array.isArray(data) && data.company_id === undefined) {
                      config.data = { ...data, company_id: String(cid) };
                    }
                  } else if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
                    if (config.data.company_id === undefined) config.data.company_id = String(cid);
                  }
                }
              }
            } catch (e) {
              // silent - do not block request on interceptor errors
            }
            return config;
          }, (error) => Promise.reject(error));
          // store on window so tests/tools can eject if needed
          if (typeof window !== 'undefined') window.__axios_company_id_interceptor = interceptorId;
        } catch (e) {
          console.error('Failed to restore axios defaults from localStorage', e);
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('userData');
        localStorage.removeItem('authToken');
      }
    } else {
      console.log('UserContext - No existing session found');
    }
  }, []);

  const login = (userData, token) => {
    console.log('UserContext - Login called with:', { userData, token });
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('authToken', token);
    localStorage.setItem('userType', userData?.user_type);
    localStorage.setItem('userData', JSON.stringify(userData));
    localStorage.removeItem('companies');
    console.log('UserContext - User state updated:', userData);
    // set axios defaults so subsequent requests include auth and company
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const companyId = userData?.company_id || userData?.company?.company_id || userData?.company?.id || userData?.companyId;
      const previousCompanyId = localStorage.getItem('activeCompanyId') || localStorage.getItem('company_id');
      if (String(previousCompanyId || '') !== String(companyId || '')) {
        localStorage.removeItem('activeStoreId');
        localStorage.removeItem('store_id');
        delete axios.defaults.headers.common['store_id'];
        localStorage.removeItem('companyInfo');
      }
      if (companyId) {
        axios.defaults.headers.common['company_id'] = String(companyId);
        try { localStorage.setItem('company_id', String(companyId)); } catch (e) {}
        try { localStorage.setItem('activeCompanyId', String(companyId)); } catch (e) {}
      } else {
        delete axios.defaults.headers.common['company_id'];
        localStorage.removeItem('company_id');
        localStorage.removeItem('activeCompanyId');
      }
    } catch (e) {
      console.error('Failed to set axios defaults on login', e);
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    // clear axios defaults
    try {
      delete axios.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['company_id'];
    } catch (e) {}
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('userData', JSON.stringify(userData));
    // ensure axios uses current company_id
    try {
      const companyId = userData?.company_id || JSON.parse(localStorage.getItem('activeCompanyId') || 'null') || JSON.parse(localStorage.getItem('company_id') || 'null');
      if (companyId) {
        axios.defaults.headers.common['company_id'] = String(companyId);
        try { localStorage.setItem('company_id', String(companyId)); } catch (e) {}
        try { localStorage.setItem('activeCompanyId', String(companyId)); } catch (e) {}
      } else {
        delete axios.defaults.headers.common['company_id'];
      }
    } catch (e) {
      console.error('Failed to update axios company_id header', e);
    }
  };

  const setActiveCompany = (companyId, companyInfo = null) => {
    try {
      const currentCompanyId = localStorage.getItem('activeCompanyId') || localStorage.getItem('company_id');
      if (String(currentCompanyId || '') !== String(companyId)) {
        localStorage.removeItem('activeStoreId');
        localStorage.removeItem('store_id');
        delete axios.defaults.headers.common['store_id'];
      }
      if (companyInfo) {
        setUser((u) => ({ ...u, company: companyInfo, company_id: String(companyId) }));
        localStorage.setItem('companyInfo', JSON.stringify(companyInfo));
      }
      setUser((u) => (u ? { ...u, company_id: String(companyId) } : u));
      try { localStorage.setItem('company_id', String(companyId)); } catch (e) {}
      try { localStorage.setItem('activeCompanyId', String(companyId)); } catch (e) {}
      axios.defaults.headers.common['company_id'] = String(companyId);
    } catch (e) {
      console.error('setActiveCompany failed', e);
    }
  };

  return (
    <UserContext.Provider value={{
      user,
      isAuthenticated,
      login,
      logout,
      updateUser,
      setActiveCompany
    }}>
      {children}
    </UserContext.Provider>
  );
};
